// miniapp/src/components/admin/CannedResponseImport.tsx
//
// Phase 37 (Canned Responses) Plan 07 — the CSV bulk-import panel mounted in
// CannedAdminScreen (37-05's csv-import-slot).
//
// Two-step UPSERT flow against the 37-06 backend:
//   file picker -> validate (dry-run, writes nothing) -> per-row preview
//   (each row badged New / Update / will-create-category / error) -> Confirm
//   (disabled while ANY row has an error, or no file/preview) -> commit
//   (atomic upsert) -> summary (created / updated / reactivated counts).
//
// The import is an UPSERT keyed on (category + title): re-uploading a CSV
// UPDATES existing answers and ADDS new ones — it NEVER deletes answers that are
// absent from the file. A "Download CSV template" affordance fetches the
// header + the seeded sample row so admins fill the right shape.
//
// The preview columns reflect the CSV shape
// (category / title / body / sort_order).

import { useRef, useState } from 'react';
import {
  Upload,
  Download,
  AlertTriangle,
  CheckCircle,
  Loader2,
  FileText,
} from 'lucide-react';
import { useCannedResponses } from '../../hooks/useCannedResponses';
import type { ImportPreviewRow } from '../../types';

// Compact in-app preview of the CSV shape — rendered as a wrapped 4-column table
// (NOT the raw downloaded file) so admins can see the format without opening the
// CSV in a tiny device viewer. Mirrors the 37-06 template: the columns + a short
// sample row.
const TEMPLATE_COLUMNS = ['category', 'title', 'body', 'sort_order'] as const;
const TEMPLATE_SAMPLE: Record<(typeof TEMPLATE_COLUMNS)[number], string> = {
  category: 'ការផ្ទៀងផ្ទាត់',
  title: 'ការផ្ទៀងផ្ទាត់ / SIM',
  body: 'សរសេរអត្ថបទចម្លើយរបស់អ្នកនៅទីនេះ។ អាចសរសេរបានច្រើនបន្ទាត់។',
  sort_order: '0',
};

interface CannedResponseImportProps {
  /**
   * Called after a successful commit so the parent admin screen (which holds
   * its OWN useCannedResponses instance) re-loads its category/answer lists.
   * This panel's hook instance already refreshes its own copies on commit, but
   * the parent's separate instance needs this nudge to re-render the library.
   */
  onImported?: () => void;
}

export function CannedResponseImport({ onImported }: CannedResponseImportProps) {
  const {
    importPreview,
    importSummary,
    isImporting,
    importError,
    validateImport,
    commitImport,
    downloadTemplate,
    resetImport,
  } = useCannedResponses();

  // The File chosen in the picker — kept so Confirm can re-send the same bytes
  // to the commit pass.
  const [file, setFile] = useState<File | null>(null);
  // Replace mode (OPT-IN, default OFF): when on, the import MIRRORS the file —
  // active answers not in the file are deactivated (reversible). Toggling it
  // re-runs validate so the preview's toRemove set updates.
  const [replace, setReplace] = useState(false);
  const [templateError, setTemplateError] = useState<string | null>(null);
  // Shown after a successful template save so the user knows it worked (the
  // silent device-save returns no path, so we confirm in-app).
  const [templateSaved, setTemplateSaved] = useState(false);
  // The template-format table is tucked behind a "View format" toggle so the
  // idle panel stays compact (collapsed by default).
  const [showFormat, setShowFormat] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const preview = importPreview?.preview ?? [];
  const headerErrors = importPreview?.errors ?? [];
  const rowErrorCount = preview.filter((r) => r.tag === 'error').length;
  const hasAnyError = !importPreview?.ok || headerErrors.length > 0 || rowErrorCount > 0;

  const newCount = preview.filter((r) => r.tag === 'New').length;
  const updateCount = preview.filter((r) => r.tag === 'Update').length;
  // Distinct categories that will be created (newCategory is a per-row flag, so
  // dedupe by category name — N rows under one new category = 1 new category).
  const newCategoryCount = new Set(
    preview.filter((r) => r.newCategory).map((r) => r.category),
  ).size;
  // Replace-mode "to remove" set (only populated when replace was on during the
  // validate pass). Each entry is an active answer a commit would deactivate.
  const toRemove = importPreview?.toRemove ?? [];
  const removeCount = toRemove.length;

  // Confirm is gated: no file, no preview yet, an in-flight request, or ANY
  // row/header error all block the commit. The validate pass NEVER commits.
  const canCommit = !!file && !!importPreview && !hasAnyError && !isImporting;

  const handlePickFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0] ?? null;
    // Picking a new file clears any prior preview/summary/error first.
    resetImport();
    setTemplateError(null);
    setTemplateSaved(false);
    setFile(picked);
    if (picked) {
      await validateImport(picked, replace); // dry-run only — writes nothing
    }
  };

  // Toggling Replace mode re-runs validate with the new flag so the preview's
  // toRemove set (and the counts) update immediately.
  const handleToggleReplace = async () => {
    const next = !replace;
    setReplace(next);
    if (file) {
      await validateImport(file, next);
    }
  };

  const handleConfirm = async () => {
    if (!file) return;
    const result = await commitImport(file, replace);
    if (result.ok) {
      onImported?.();
      // Clear the file input so the same file can be re-picked if needed.
      if (fileInputRef.current) fileInputRef.current.value = '';
      setFile(null);
    }
    // On a 422 reject the hook stuffs the row errors back into importPreview,
    // so the preview re-renders the per-row errors (no crash).
  };

  const handleReset = () => {
    resetImport();
    setTemplateError(null);
    setTemplateSaved(false);
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDownloadTemplate = async () => {
    setTemplateError(null);
    setTemplateSaved(false);
    const result = await downloadTemplate();
    if (result.ok) setTemplateSaved(true);
    else setTemplateError(result.error);
  };

  return (
    <section>
      {/* Section header — matches the Categories / Answer Library pattern:
          uppercase title + an inline action link on the right. */}
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-sm font-semibold text-text-primary uppercase tracking-wide">
          Import CSV
        </h2>
        <button
          type="button"
          onClick={handleDownloadTemplate}
          className="flex items-center gap-1 text-sm font-medium text-[#2563EB] touch-active"
        >
          <Download className="h-4 w-4" aria-hidden /> Template
        </button>
      </div>

      <div className="rounded-xl border border-border-primary bg-white p-3 space-y-3">
        {/* One concise helper line. */}
        <p className="text-xs text-text-secondary leading-snug">
          Adds new answers and updates existing ones by category + title.
          {replace ? '' : ' Never deletes.'}
        </p>

        {templateError && (
          <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
            {templateError}
          </div>
        )}

        {templateSaved && (
          <div className="rounded-lg bg-green-50 border border-green-200 px-3 py-2 text-sm text-green-800 flex items-start gap-2">
            <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" aria-hidden />
            <span>Template saved to your device — open it in the Files app (or Google Sheets / Excel) to fill it in.</span>
          </div>
        )}

        {/* Choose file + a compact "View format" disclosure. */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <label className="flex items-center gap-2 rounded-xl border border-border-primary bg-white px-3 py-2.5 text-sm font-medium text-text-primary cursor-pointer hover:bg-gray-50 touch-active">
            <FileText className="h-4 w-4 text-text-secondary" aria-hidden />
            {file ? 'Choose a different file' : 'Choose CSV file'}
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              onChange={handlePickFile}
              className="hidden"
              disabled={isImporting}
            />
          </label>
          <button
            type="button"
            onClick={() => setShowFormat((v) => !v)}
            className="text-sm font-medium text-[#2563EB] touch-active"
            aria-expanded={showFormat}
          >
            {showFormat ? 'Hide format' : 'View format'}
          </button>
          {file && (
            <span className="text-xs text-text-secondary truncate min-w-0">{file.name}</span>
          )}
          {isImporting && (
            <Loader2 className="h-4 w-4 animate-spin text-text-secondary" aria-hidden />
          )}
        </div>

        {/* Replace-mode toggle (OPT-IN, default off). A compact checkbox row
            with a one-line caption. Toggling re-runs validate so the preview
            reflects what would be removed. */}
        <label className="flex items-start gap-2 cursor-pointer touch-active">
          <input
            type="checkbox"
            checked={replace}
            onChange={handleToggleReplace}
            disabled={isImporting}
            className="mt-0.5 h-4 w-4 rounded border-border-primary text-[#2563EB] focus:ring-[#2563EB]/40 disabled:opacity-50"
          />
          <span className="min-w-0">
            <span className="block text-sm font-medium text-text-primary">
              Replace — remove answers not in this file
            </span>
            <span className="block text-xs text-text-secondary leading-snug">
              Answers missing from the file are deactivated (hidden), not erased — reversible by re-adding them.
            </span>
          </span>
        </label>

        {/* Collapsible template-format preview — a compact wrapped table
            (table-fixed + break-words; nothing scrolls). Hidden by default. */}
        {showFormat && (
          <div className="rounded-lg border border-border-primary overflow-hidden">
            <table className="w-full table-fixed text-xs">
              <colgroup>
                <col className="w-[24%]" />
                <col className="w-[24%]" />
                <col className="w-[36%]" />
                <col className="w-[16%]" />
              </colgroup>
              <thead>
                <tr className="bg-gray-50 text-left text-text-secondary border-b border-border-primary">
                  {TEMPLATE_COLUMNS.map((c) => (
                    <th key={c} className="px-2 py-1.5 font-medium align-top break-words whitespace-normal">
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr className="align-top">
                  {TEMPLATE_COLUMNS.map((c) => (
                    <td key={c} className="px-2 py-1.5 text-text-primary align-top break-words whitespace-normal">
                      {TEMPLATE_SAMPLE[c]}
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
            <p className="px-2 py-1.5 text-[11px] text-text-secondary border-t border-border-primary leading-snug">
              <span className="font-medium">sort_order</span> optional · <span className="font-medium">body</span> can be multi-line
            </p>
          </div>
        )}

      {/* Non-row import error (network / 403 / unexpected). */}
      {importError && !importSummary && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
          {importError}
        </div>
      )}

      {/* Commit summary (created / updated / reactivated). */}
      {importSummary && (
        <div className="rounded-lg bg-green-50 border border-green-200 px-3 py-2.5 text-sm text-green-800 flex items-start gap-2">
          <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" aria-hidden />
          <div>
            Imported{' '}
            {importSummary.createdAnswers + importSummary.updatedAnswers} answers (
            {importSummary.createdAnswers} new, {importSummary.updatedAnswers} updated
            {importSummary.reactivatedAnswers > 0
              ? `, ${importSummary.reactivatedAnswers} reactivated`
              : ''}
            {importSummary.removedAnswers > 0
              ? `, ${importSummary.removedAnswers} removed`
              : ''}
            )
            {importSummary.createdCategories.length > 0
              ? `, created ${importSummary.createdCategories.length} categor${
                  importSummary.createdCategories.length === 1 ? 'y' : 'ies'
                }`
              : ''}
            .
          </div>
        </div>
      )}

      {/* Header / file-level fatal errors (bad header, empty file, oversize). */}
      {headerErrors.length > 0 && (
        <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700 space-y-1">
          {headerErrors.map((e, i) => (
            <div key={i} className="flex items-start gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" aria-hidden />
              <span>
                {e.row > 0 ? `Row ${e.row}` : 'File'}
                {e.column ? ` · ${e.column}` : ''}: {e.reason}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Per-row preview (only after a validate pass with rows). */}
      {importPreview && preview.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center gap-2 text-xs text-text-secondary">
            <span className="font-medium">{preview.length} rows</span>
            {newCount > 0 && <span>· {newCount} new</span>}
            {updateCount > 0 && <span>· {updateCount} update</span>}
            {newCategoryCount > 0 && (
              <span>· creates {newCategoryCount} categor{newCategoryCount === 1 ? 'y' : 'ies'}</span>
            )}
            {removeCount > 0 && (
              <span className="text-amber-700 font-medium">· {removeCount} to remove</span>
            )}
            {rowErrorCount > 0 && (
              <span className="text-red-600 font-medium">· {rowErrorCount} error</span>
            )}
          </div>

          <div className="overflow-x-auto rounded-xl border border-border-primary">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 text-left text-xs text-text-secondary">
                  <th className="px-2 py-1.5 font-medium w-10">#</th>
                  <th className="px-2 py-1.5 font-medium">Status</th>
                  <th className="px-2 py-1.5 font-medium">category</th>
                  <th className="px-2 py-1.5 font-medium">title</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border-primary">
                {preview.map((row) => (
                  <PreviewRow key={row.row} row={row} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Confirm / reset actions (shown once a file is picked). */}
      {file && (
        <div className="flex items-center gap-3 pt-1">
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!canCommit}
            className="flex items-center justify-center gap-2 rounded-xl bg-[#2563EB] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#1D4ED8] active:bg-[#1E40AF] disabled:opacity-40 disabled:cursor-not-allowed touch-active"
            title={
              hasAnyError
                ? 'Fix the row errors before importing'
                : undefined
            }
          >
            {isImporting ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <Upload className="h-4 w-4" aria-hidden />
            )}
            {importPreview && !hasAnyError
              ? `Import (${newCount} new, ${updateCount} update${
                  removeCount > 0 ? `, ${removeCount} remove` : ''
                })`
              : 'Import'}
          </button>
          <button
            type="button"
            onClick={handleReset}
            disabled={isImporting}
            className="rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-900 hover:bg-gray-50 active:bg-gray-100 disabled:opacity-50 touch-active"
          >
            Reset
          </button>
        </div>
      )}
      </div>
    </section>
  );
}

// ── One preview row with its status badge ──────────────────────────────────
function PreviewRow({ row }: { row: ImportPreviewRow }) {
  return (
    <tr className={row.tag === 'error' ? 'bg-red-50/60' : undefined}>
      <td className="px-2 py-1.5 text-xs text-text-secondary align-top">{row.row}</td>
      <td className="px-2 py-1.5 align-top">
        <StatusBadge row={row} />
      </td>
      {row.tag === 'error' ? (
        <td className="px-2 py-1.5 text-xs text-red-700 align-top" colSpan={2}>
          {row.column ? `${row.column}: ` : ''}
          {row.reason}
        </td>
      ) : (
        <>
          <td className="px-2 py-1.5 text-text-primary align-top break-words">
            {row.category ?? '—'}
            {row.newCategory && (
              <span className="ml-1 align-middle text-[9px] font-semibold uppercase px-1 py-0.5 rounded bg-amber-100 text-amber-800">
                new
              </span>
            )}
          </td>
          <td className="px-2 py-1.5 text-text-primary align-top break-words">
            {row.title ?? '—'}
          </td>
        </>
      )}
    </tr>
  );
}

function StatusBadge({ row }: { row: ImportPreviewRow }) {
  // green = New, blue = Update, red = error. (The "new category" marker is a
  // separate per-row pill rendered next to the category name.)
  const map: Record<ImportPreviewRow['tag'], { cls: string; label: string }> = {
    New: { cls: 'bg-green-100 text-green-700', label: 'New' },
    Update: { cls: 'bg-blue-100 text-blue-700', label: 'Update' },
    error: { cls: 'bg-red-100 text-red-700', label: 'Error' },
  };
  const { cls, label } = map[row.tag];
  return (
    <span
      className={`inline-block text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded whitespace-nowrap ${cls}`}
    >
      {label}
    </span>
  );
}
